import { useCallback, useMemo, useState } from 'react';
import { TechnicalPlanTable } from '@bulthaup/api/db';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  Grid,
  Group,
  Stack,
  Textarea,
  Title,
} from '@mantine/core';
import { IconBolt } from '@tabler/icons-react';
import { pickBy } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { RouterOutput } from '../../../../../../services/trpc';
import { CategorySection } from './category-section';
import { CutSelectionCheckboxes } from './cut-selection-checkboxes';

const predefinedSentences = [
  '**APPAREILLAGE** MANQUANT DANS TOUTE LA CUISINE',
  "**ARRIVEE EAU** FROIDE AVEC ROBINET D'ARRET **SORTIE** VERTICALE **VERS LE BAS** EST A PLUS DE 6 CM DU MUR EN SAILLIE (AU POSTE ZE1)",
  '**LA PLOMBERIE** EST A PLUS DE 6 CM DU MUR EN SAILLIE (AU POSTE **AM**) CETTE CONTRAINTE IMPLIQUE UNE MODIFICATION DE LA CUISINE **A LA CHARGE DU CLIENT**',
  "**LE FLEXIBLE D'EVACUATION** DE LA HOTTE ETANT D'UN **DIAMETRE INFERIEUR** A CELUI DEMANDE L'ASPIRATION N'ATTEINDRA PAS SA PUISSANCE MAXIMUM",
  "**L' EVACUATION** DE LA HOTTE N'EST PAS A L'EMPLACEMENT INDIQUE",
  '**LES MURS** VUE **BB** ET **CC** NE SONT PAS AUX DIMENSIONS INDIQUEES', // This will be dynamic
  "**LE COMPTEUR** EDF N'EST PAS A LA HAUTEUR INDIQUEE",
  "**LE FAUX PLAFOND** N' EST PAS A LA HAUTEUR CONVENUE LE FUT DE LA HOTTE DOIT ETRE MOFIIFE",
  "**LE POSITIONNEMENT** DU **RADIATEUR SOUS LA FENETRE** IMPLIQUE L'IMPOSIBILITE DE POSER LA CUISINE",
];

const WALLS_SENTENCE_INDEX = 5; // Index of the walls sentence in predefinedSentences

// Helper function to convert asterisks to bold formatting for display
const formatBoldText = (text: string) => {
  // Regex captures **bold** or *bold* with optional leading/trailing spaces
  return text.split(/(\s*\*{1,2}[^*]+\*{1,2}\s*)/).map((part, index) => {
    if (!part) return null;
    const match = part.match(/^(\s*)\*{1,2}([^*]+)\*{1,2}(\s*)$/);
    if (match) {
      // match[1] = leading spaces, match[2] = bold text, match[3] = trailing spaces
      return (
        <span key={index}>
          {match[1]}
          <strong>{match[2]}</strong>
          {match[3]}
        </span>
      );
    }
    return part;
  });
};

// Zod schema for validation
const constructionSiteReviewSchema = z.object({
  table: z.record(
    z.object({
      state: z.string(),
      cut: z.string(),
    }),
  ),
  predefinedSentences: z.array(z.string()),
  importantNotes: z.string(),
});

export type ConstructionSiteReviewFormValues = z.infer<
  typeof constructionSiteReviewSchema
>;

interface CategoryElements {
  plumbing: string[];
  electricity: string[];
  masonry: string[];
}

export default function ConstructionSiteReviewForm({
  project,
  constructionSiteReview,
  onSubmit,
}: {
  project: NonNullable<RouterOutput['projects']['get']>;
  constructionSiteReview: ConstructionSiteReviewFormValues | null;
  onSubmit: (values: ConstructionSiteReviewFormValues) => Promise<void>;
}) {
  // State for selected cuts for the walls sentence
  const [selectedWallsCuts, setSelectedWallsCuts] = useState<string[]>([]);

  // Function to generate the dynamic walls sentence
  const generateWallsSentence = useCallback((cuts: string[]) => {
    if (cuts.length === 0) {
      return '**LES MURS** NE SONT PAS AUX DIMENSIONS INDIQUEES';
    } else if (cuts.length === 1) {
      return `**LES MURS** VUE **${cuts[0]}** NE SONT PAS AUX DIMENSIONS INDIQUEES`;
    } else if (cuts.length === 2) {
      return `**LES MURS** VUES **${cuts[0]}** ET **${cuts[1]}** NE SONT PAS AUX DIMENSIONS INDIQUEES`;
    } else {
      const lastCut = cuts[cuts.length - 1];
      const otherCuts = cuts.slice(0, -1);
      return `**LES MURS** VUES **${otherCuts.join('**, **')}** ET **${lastCut}** NE SONT PAS AUX DIMENSIONS INDIQUEES`;
    }
  }, []);

  // Extract selected elements from technical plan table grouped by category
  const categoryElements = useMemo<CategoryElements>(() => {
    if (!project.technicalPlanTable) {
      return { plumbing: [], electricity: [], masonry: [] };
    }

    const technicalPlanTable = project.technicalPlanTable as TechnicalPlanTable;

    return {
      plumbing: Object.keys(pickBy(technicalPlanTable.plumbing, Boolean)),
      electricity: Object.keys(pickBy(technicalPlanTable.electricity, Boolean)),
      masonry: Object.keys(pickBy(technicalPlanTable.masonry, Boolean)),
    };
  }, [project.technicalPlanTable]);

  // Memoize allElements to prevent recalculation
  const allElements = useMemo(
    () => [
      ...categoryElements.plumbing,
      ...categoryElements.electricity,
      ...categoryElements.masonry,
    ],
    [categoryElements],
  );

  // Memoize default values to prevent form recreation
  const defaultValues = useMemo(
    () =>
      constructionSiteReview || {
        table: allElements.reduce(
          (acc, element) => ({
            ...acc,
            [element]: {
              state: '',
              cut: '',
            },
          }),
          {},
        ),
        predefinedSentences: [],
        importantNotes: '',
      },
    [constructionSiteReview, allElements],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ConstructionSiteReviewFormValues>({
    resolver: zodResolver(constructionSiteReviewSchema),
    defaultValues,
  });

  // Watch the predefined sentences to update them when walls cuts change
  const watchedSentences = watch('predefinedSentences');

  // Update predefined sentences with dynamic walls sentence
  const dynamicPredefinedSentences = useMemo(() => {
    const sentences = [...predefinedSentences];
    sentences[WALLS_SENTENCE_INDEX] = generateWallsSentence(selectedWallsCuts);
    return sentences;
  }, [selectedWallsCuts, generateWallsSentence]);

  // Update the form when walls cuts change
  const handleWallsCutsChange = useCallback(
    (newCuts: string[]) => {
      setSelectedWallsCuts(newCuts);

      // If the walls sentence is currently selected, update it in the form
      const newWallsSentence = generateWallsSentence(newCuts);

      const currentSentences = watchedSentences || [];
      const hasOldSentence = currentSentences.some(
        sentence =>
          sentence.startsWith('**LES MURS**') &&
          sentence.includes('NE SONT PAS AUX DIMENSIONS INDIQUEES'),
      );

      if (hasOldSentence) {
        const updatedSentences = currentSentences.map(sentence =>
          sentence.startsWith('**LES MURS**') &&
          sentence.includes('NE SONT PAS AUX DIMENSIONS INDIQUEES')
            ? newWallsSentence
            : sentence,
        );
        setValue('predefinedSentences', updatedSentences);
      }
    },
    [generateWallsSentence, watchedSentences, setValue],
  );

  const handleFormSubmit = useCallback(
    async (values: ConstructionSiteReviewFormValues) => {
      await onSubmit(values);
    },
    [onSubmit],
  );

  // Check if walls sentence is selected
  const isWallsSentenceSelected = useMemo(() => {
    return (
      watchedSentences?.some(
        sentence =>
          sentence.startsWith('**LES MURS**') &&
          sentence.includes('NE SONT PAS AUX DIMENSIONS INDIQUEES'),
      ) || false
    );
  }, [watchedSentences]);

  // Memoize predefined sentences display
  const predefinedSentencesSection = useMemo(
    () => (
      <Stack gap="xs">
        <Title order={4}>Phrases à ajouter</Title>
        <Grid gutter="xs">
          {dynamicPredefinedSentences.map((sentence, index) => (
            <Grid.Col span={12} key={index}>
              <Controller
                name="predefinedSentences"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value.includes(sentence)}
                    onChange={event => {
                      if (event.currentTarget.checked) {
                        field.onChange([...field.value, sentence]);
                      } else {
                        field.onChange(
                          field.value.filter((s: string) => s !== sentence),
                        );
                      }
                    }}
                    onBlur={field.onBlur}
                    label={<span>{formatBoldText(sentence)}</span>}
                    size="sm"
                  />
                )}
              />

              {/* Show cuts selection for walls sentence */}
              {index === WALLS_SENTENCE_INDEX && isWallsSentenceSelected && (
                <CutSelectionCheckboxes
                  selectedCuts={selectedWallsCuts}
                  onCutsChange={handleWallsCutsChange}
                />
              )}
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    ),
    [
      dynamicPredefinedSentences,
      control,
      isWallsSentenceSelected,
      selectedWallsCuts,
      handleWallsCutsChange,
    ],
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack gap="xl">
        {/* Header */}
        <Title order={3}>Compte-rendu de chantier</Title>

        {/* Construction Elements */}
        <Stack gap="lg">
          <CategorySection
            title="Plomberie"
            elements={categoryElements.plumbing}
            control={control}
            errors={errors}
          />
          <CategorySection
            title="Électricité"
            elements={categoryElements.electricity}
            control={control}
            errors={errors}
          />
          <CategorySection
            title="Maçonnerie"
            elements={categoryElements.masonry}
            control={control}
            errors={errors}
          />
        </Stack>

        {predefinedSentencesSection}

        {/* Important Notes */}
        <Stack gap="xs">
          <Title order={4}>Notes importantes</Title>
          <Controller
            name="importantNotes"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                placeholder="Saisir les observations importantes, problèmes rencontrés, ou points d'attention..."
                rows={6}
                error={errors.importantNotes?.message}
              />
            )}
          />
        </Stack>

        {/* Submit Button */}
        <Group justify="center" mt="xl">
          <Button
            leftSection={<IconBolt size={16} />}
            type="submit"
            name="validate"
            size="md"
            loading={isSubmitting}
          >
            Générer le compte-rendu
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
